import { useEffect, useRef, useState } from "react"

const ACCEPTED = "image/png,image/jpeg,image/svg+xml"
const MAX_BYTES = 2 * 1024 * 1024

function validate(file) {
  if (!file) return null
  if (!ACCEPTED.split(",").includes(file.type)) {
    return "Logo must be PNG, JPEG, or SVG."
  }
  if (file.size > MAX_BYTES) {
    return "Logo is too large. Keep it under 2 MB."
  }
  return null
}

const focusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight"

const thumbTile =
  "w-12 h-12 flex-shrink-0 rounded bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm"

const actionBtn =
  "inline-flex items-center justify-center min-h-[44px] px-3 rounded text-sm font-semibold transition " + focusRing

// The Logo box. It has two states, and the box itself is the feedback (#266):
//
//   empty   one dashed drop zone that opens the file picker.
//   filled  the logo's thumbnail and file name, with Replace and Remove.
//
// `file` is a freshly picked File the caller has not saved yet; `previewUrl` is
// the saved logo. A caller that saves as you go (QrBuilder) drops the File once
// it is stored, so it hands the name on as `fileName`. With a saved URL and no
// name, the box says "Logo added" and leaves it there.
export default function LogoUpload({ file, fileName = null, previewUrl, onChange, onRemove }) {
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const zoneRef = useRef(null)
  const replaceRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState(null)
  const [announcement, setAnnouncement] = useState("")
  const [fileUrl, setFileUrl] = useState(null)

  // One object URL per picked File, released when the File changes or the box
  // unmounts. This used to mint a new one on every render and free none.
  useEffect(() => {
    if (!file) { setFileUrl(null); return }
    const url = URL.createObjectURL(file)
    setFileUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const hasLogo = !!(file || previewUrl)
  // A new File has no URL until the effect above runs. The saved logo stays up
  // for that one frame, so Replace does not blink an empty tile.
  const thumb = (file && fileUrl) || previewUrl
  const name = file ? file.name : fileName

  const openPicker = () => inputRef.current?.click()

  // The drop zone and the filled box are different elements, so the control
  // that had focus is gone after a pick or a removal. Hand focus to the one
  // that took its place, or a keyboard user lands back at the top of the page.
  // Only when focus was in this box: a file dragged in from the desktop must
  // not pull focus out of whatever field the visitor was in.
  //
  // Done in an effect on the empty/filled change and not on a timer: the new
  // control is in the DOM by then, however long the parent takes to answer.
  const refocusAfterSwap = useRef(false)
  const expectSwap = () => {
    refocusAfterSwap.current = !!rootRef.current?.contains(document.activeElement)
  }
  useEffect(() => {
    if (!refocusAfterSwap.current) return
    refocusAfterSwap.current = false
    ;(hasLogo ? replaceRef.current : zoneRef.current)?.focus()
  }, [hasLogo])

  const accept = (next) => {
    const err = validate(next)
    setError(err)
    if (err) return
    setAnnouncement(`${hasLogo ? "Logo replaced" : "Logo added"}: ${next.name}`)
    // Replace keeps the filled box, and the browser returns focus to Replace.
    if (!hasLogo) expectSwap()
    onChange(next)
  }

  const remove = () => {
    setError(null)
    setAnnouncement("Logo removed")
    expectSwap()
    onRemove()
  }

  // Both states take a dropped file, so dragging a new one onto a filled box
  // replaces the logo.
  const dropTarget = {
    onDragOver: (e) => { e.preventDefault(); setDragOver(true) },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => {
      e.preventDefault()
      setDragOver(false)
      const next = e.dataTransfer.files?.[0]
      if (next) accept(next)
    }
  }

  return (
    <div ref={rootRef}>
      {hasLogo ? (
        // Not a role="button" like the empty zone: it holds two real buttons,
        // and a button inside a button is unreachable for assistive tech.
        <div
          {...dropTarget}
          className={
            "p-3 rounded border transition " +
            (dragOver
              ? "border-voltage bg-voltage/5"
              : "border-[color:var(--color-border)] dark:border-storm")
          }
        >
          <div className="flex items-center gap-3">
            {thumb
              ? <img src={thumb} alt="" draggable={false} className={thumbTile + " object-contain"} />
              : <span className={thumbTile} />}
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-sm font-semibold">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 text-voltage-dark dark:text-voltage-light">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Logo added
              </p>
              {name && (
                <p className="mt-0.5 font-mono text-xs text-[color:var(--color-muted)] dark:text-muted-light truncate" title={name}>
                  {name}
                </p>
              )}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              ref={replaceRef}
              type="button"
              onClick={openPicker}
              aria-label="Replace logo"
              className={actionBtn + " border border-[color:var(--color-border)] dark:border-storm hover:border-voltage"}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={remove}
              aria-label="Remove logo"
              className={actionBtn + " text-[color:var(--color-fail)] hover:bg-fail/10"}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={zoneRef}
          role="button"
          tabIndex={0}
          onClick={openPicker}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPicker() } }}
          {...dropTarget}
          className={
            "flex items-center gap-3 p-3 rounded border-2 border-dashed cursor-pointer transition " + focusRing + " " +
            (dragOver
              ? "border-voltage bg-voltage/5"
              : "border-[color:var(--color-border)] dark:border-storm hover:border-voltage")
          }
        >
          <span aria-hidden="true" className="w-12 h-12 flex-shrink-0 rounded bg-[color:var(--color-surface-2,#F4F4F5)] dark:bg-coal flex items-center justify-center text-[color:var(--color-muted)] dark:text-muted-light">
            +
          </span>
          <span className="text-sm">Drop a PNG, JPEG, or SVG (under 2 MB)</span>
        </div>
      )}

      {/* Outside both boxes, and out of the tab order: the drop zone and
          Replace are the ways in, and an invisible third stop helps nobody. */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const next = e.target.files?.[0]
          // Emptied after every pick, so the same file can be picked again
          // after a removal, and cancelling the dialog on Replace has no
          // selection to clear (which would otherwise discard the logo).
          e.target.value = ""
          if (next) accept(next)
        }}
        className="sr-only"
      />

      <p className="sr-only" aria-live="polite">{announcement}</p>

      {error && (
        <p role="alert" className="mt-2 text-sm text-[color:var(--color-fail)]">{error}</p>
      )}
    </div>
  )
}
